<script setup lang="ts">
  import TopBar from '@/components/TopBar.vue'
  import { useUserValidation } from '@/composables/validation/use-user-validation'
  import { useUserStore } from '@/store/user.store'
  import { NestUserRole, type NestUser } from '@/types/user'
  import {
    ConfirmationDialog,
    DropDown,
    FormFieldContainer,
    FormInput,
    UiButton,
    deepEqual,
    isUuid,
    require,
    useToastStore,
  } from '@logistiek-zonder-papier/lzp-library'
  import { isAxiosError } from 'axios'
  import { computed, onMounted, ref, toRaw } from 'vue'
  import { useRoute, useRouter } from 'vue-router'

  const toastStore = useToastStore()
  const userStore = useUserStore()
  const router = useRouter()
  const route = useRoute()

  const user = ref<NestUser | null>(null)
  const originalUser = ref<NestUser | null>(null)
  const self = ref<boolean>(false)

  const availableRoles = computed(() => Object.values(NestUserRole).map(role => ({ role })))

  const { validate, visibleErrors } = useUserValidation(user)
  const changed = computed(() => !deepEqual(originalUser.value, user.value))
  const submitting = ref<boolean>(false)
  const deletingUser = ref<boolean>(false)

  const errorMessage = ref<string | null>(null)

  onMounted(async () => {
    const userId = route.params['id']

    if (!isUuid(userId)) throw Error(`${userId} is not a valid UUID format`)

    user.value = await userStore.fetchUser(userId)
    originalUser.value = structuredClone(toRaw(user.value))
    const me = await userStore.fetchMe()

    self.value = user.value.id === me.id
  })

  async function resetPassword() {
    submitting.value = true
    await userStore.resetPassword(
      require(user.value, 'User cannot be null when resetting password').email
    )
    toastStore.success('Email succesvol verstuurd')
    submitting.value = false
  }

  async function saveUser() {
    if (!validate()) return
    submitting.value = true
    await userStore.update(require(user.value, 'User cannot be null when saving'))
    originalUser.value = structuredClone(toRaw(user.value))
    toastStore.success('Succesvol opgeslagen')
    submitting.value = false
  }

  async function deleteUser() {
    deletingUser.value = false
    errorMessage.value = null
    try {
      await userStore.deleteUser(require(user.value, 'User cannot be null when deleting a user').id)
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === 403) {
        errorMessage.value = 'Je hebt niet de juiste rol om deze gebruiker te verwijderen'
      }
    }
    toastStore.success('Gebruiker succesvol verwijderd')
    await router.replace('/users')
  }
</script>

<template>
  <TopBar />
  <ConfirmationDialog
    v-model:show="deletingUser"
    :confirm="deleteUser"
    title="Weet je het zeker?"
    body="Deze gebruiker verwijderen kan niet ongedaan gemaakt worden."
  />
  <div class="layout">
    <div v-if="user">
      <FormInput
        type="text"
        label="Voornaam"
        v-model="user.firstName"
        :error="visibleErrors.firstName"
      />

      <FormInput
        type="text"
        label="Acthernaam"
        v-model="user.lastName"
        :error="visibleErrors.lastName"
      />

      <FormInput type="text" label="E-mail" v-model="user.email" :error="visibleErrors.email" />

      <FormFieldContainer label="Rol" :error="visibleErrors.role">
        <DropDown
          :modelValue="{ role: user.role }"
          @update:modelValue="(value: { role: NestUserRole }) => (user!.role = value.role)"
          :data="availableRoles"
          idKey="role"
        >
          <template #item="{ item }">
            <span>{{ item.role }}</span>
          </template>
        </DropDown>
      </FormFieldContainer>

      <div v-if="errorMessage" class="error-message">{{ errorMessage }}</div>

      <div class="button-group">
        <UiButton
          :disabled="!changed || submitting"
          label="Opslaan"
          @click="saveUser"
          variant="prominent"
        />
        <UiButton
          :disabled="changed || submitting"
          :label="changed ? 'Sla eerst op' : 'Verstuur wachtwoord reset email'"
          @click="resetPassword"
          variant="default"
        />
        <UiButton
          v-if="!self"
          :disabled="submitting"
          label="Verwijderen"
          icon="delete"
          @click="deletingUser = true"
          variant="negative"
        />
      </div>
    </div>
  </div>
</template>

<style lang="css" scoped>
  .layout {
    display: flex;
    padding: var(--spacing-m);
    flex-direction: column;
    gap: var(--layout-layout-spacing-app);
    flex: 1 0 0;
  }

  .button-group {
    width: 100%;
    display: flex;
    flex-direction: row;
    justify-content: end;
    gap: var(--spacing-s);
  }
</style>
